import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { CartContext } from '../context/CartContext';

import { useAuth } from '../hooks/useAuth';
import { useMobile } from '../hooks/useMobile';

import { getDeliveryRequest } from '../api/delivery/delivery.action';
import { trackEvent } from '../utils/analytics/metaPixel';
import { buildCartLineId } from '../utils/cart/cartLineId';
import { convertToAddressString } from '../utils/currency/tools';
import { getFromLocalStorage, saveToLocalStorage, STORAGE_KEYS } from '../utils/storage/storage';

export const CartProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isMobile = useMobile();
  const { user } = useAuth();
  const previousUserRef = useRef(undefined);
  const [cart, setCart] = useState();
  const [cartOpen, setCartOpen] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const order = useSelector(({ orders }) => orders.order);
  const serviceFeePercentage = 0.05;
  const debounceTimeout = useRef(null);
  const { delivery: deliveryFee, loading: deliveryLoading } = useSelector(
    ({ delivery }) => delivery
  );

  const { existingOrders } = useSelector((state) => state.orders);
  const { promotions } = useSelector((state) => state.promotions);

  const [address, setAddress] = useState(
    convertToAddressString(getFromLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS), true)
  );

  const location = useMemo(() => {
    const storedAddress = getFromLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS);
    return storedAddress?.location;
    //eslint-disable-next-line
  }, [address]);

  const memoizedCart = useMemo(
    () => ({
      items: cart?.items ?? [],
      id: cart?.id ?? uuidv4()
    }),
    [cart]
  );

  useEffect(() => {
    const storedCart = getFromLocalStorage(STORAGE_KEYS.CART);
    setCart(storedCart);
  }, []);

  const memoizedCartCount = useMemo(() => {
    return memoizedCart.items.reduce((total, item) => {
      return total + item.quantity;
    }, 0);
  }, [memoizedCart]);

  const handleSetCart = (cart) => {
    setCart(cart);
    saveToLocalStorage(STORAGE_KEYS.CART, cart);
  };

  // Clear the cart on an actual sign-out (a real user → no user), not on the initial auth
  // resolution or on signing into/out of a guest checkout session — those should keep whatever
  // was already in the cart.
  useEffect(() => {
    const previousUser = previousUserRef.current;
    if (previousUser && !user) {
      handleSetCart({ items: [], id: uuidv4() });
    }
    previousUserRef.current = user;
    //eslint-disable-next-line
  }, [user]);

  const handleAddToCart = (item) => {
    const cartLineId = item.cartLineId ?? buildCartLineId(item.id, item.selectedOptionIds);
    const addedQuantity = item.quantity ?? 1;
    trackEvent('AddToCart', {
      content_ids: [item.id],
      content_name: item.name,
      content_type: 'product',
      value: (item.price ?? 0) * addedQuantity,
      currency: 'ZAR'
    });
    const existingLine = memoizedCart.items.find((cartItem) => cartItem.cartLineId === cartLineId);

    if (existingLine) {
      handleSetCart({
        ...memoizedCart,
        items: memoizedCart.items.map((cartItem) =>
          cartItem.cartLineId === cartLineId
            ? { ...cartItem, quantity: cartItem.quantity + addedQuantity }
            : cartItem
        )
      });
      return;
    }

    handleSetCart({
      ...memoizedCart,
      items: [
        ...memoizedCart.items,
        {
          ...item,
          quantity: addedQuantity,
          cartLineId
        }
      ]
    });
  };

  const handleRemoveFromCart = (item) => {
    const cartLineId = item.cartLineId ?? buildCartLineId(item.id, item.selectedOptionIds);
    handleSetCart({
      items: memoizedCart.items.filter((cartItem) => cartItem.cartLineId !== cartLineId)
    });
  };

  const handleIncrementItem = (item) => {
    const cartLineId = item.cartLineId ?? buildCartLineId(item.id, item.selectedOptionIds);
    handleSetCart({
      items: memoizedCart.items.map((cartItem) =>
        cartItem.cartLineId === cartLineId
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      )
    });
  };

  const handleDecrementItem = (item) => {
    const cartLineId = item.cartLineId ?? buildCartLineId(item.id, item.selectedOptionIds);
    handleSetCart({
      items: memoizedCart.items
        .map((cartItem) =>
          cartItem.cartLineId === cartLineId
            ? { ...cartItem, quantity: cartItem.quantity - 1 }
            : cartItem
        )
        .filter((cartItem) => cartItem.quantity > 0)
    });
  };

  const handleUpdateCartItemOptions = (cartLineId, { selectedOptionIds, selectedOptions, priceDelta }) => {
    const existingLine = memoizedCart.items.find((cartItem) => cartItem.cartLineId === cartLineId);
    if (!existingLine) return;

    const newCartLineId = buildCartLineId(existingLine.id, selectedOptionIds);
    const basePrice = existingLine.basePrice ?? existingLine.price;
    const mergeTarget =
      newCartLineId !== cartLineId &&
      memoizedCart.items.find((cartItem) => cartItem.cartLineId === newCartLineId);

    if (mergeTarget) {
      handleSetCart({
        items: memoizedCart.items
          .filter((cartItem) => cartItem.cartLineId !== cartLineId && cartItem.cartLineId !== newCartLineId)
          .concat({ ...mergeTarget, quantity: mergeTarget.quantity + existingLine.quantity })
      });
      return;
    }

    handleSetCart({
      items: memoizedCart.items.map((cartItem) =>
        cartItem.cartLineId === cartLineId
          ? {
              ...cartItem,
              selectedOptionIds,
              selectedOptions,
              basePrice,
              price: basePrice + (priceDelta ?? 0),
              cartLineId: newCartLineId
            }
          : cartItem
      )
    });
  };

  const memoizedCartSubTotal = useMemo(() => {
    return memoizedCart.items.reduce((total, item) => {
      return total + item.price * item.quantity;
    }, 0);
  }, [memoizedCart]);

  const handleIsItemInCart = (item) => {
    const cartLineId = item.cartLineId ?? buildCartLineId(item.id, item.selectedOptionIds);
    return memoizedCart.items.find((cartItem) => cartItem.cartLineId === cartLineId);
  };

  const serviceFee = useMemo(
    () => memoizedCartSubTotal * serviceFeePercentage,
    [memoizedCartSubTotal]
  );

  // Best-effort preview only — the backend independently re-derives eligibility for every rule
  // type from real order data and corrects the actual charged amount at order creation
  // regardless of what's shown here. When more than one promotion qualifies, only the single
  // highest-discount one applies — they never stack, matching the backend's own logic.
  const isPromotionCurrentlyActive = (promotion) => {
    const now = new Date();
    // Usage-limited promotions require a logged-in account server-side (guests have no stable
    // identity to cap redemptions against). Anonymous sign-in only happens once someone actually
    // submits the guest checkout form, so before that `user` is null — treat "not yet known to be
    // logged in" as not eligible, rather than assuming a discount that likely won't materialise.
    const eligibleForGuest = promotion.usageLimit == null || (!!user && !user.isAnonymous);
    return (
      eligibleForGuest &&
      promotion.active &&
      (!promotion.startsAt || new Date(promotion.startsAt) <= now) &&
      (!promotion.endsAt || new Date(promotion.endsAt) >= now)
    );
  };

  const memoizedCartDiscount = useMemo(() => {
    const hasPaidOrder = existingOrders.filter((item) => item?.paid).length > 0;

    return promotions.filter(isPromotionCurrentlyActive).reduce((best, promotion) => {
      const rate = (promotion.discount ?? 0) / 100;
      let amount = 0;

      if (promotion.ruleType === 'FIRST_ORDER') {
        amount = hasPaidOrder ? 0 : memoizedCartSubTotal * rate;
      } else if (promotion.ruleType === 'MIN_ORDER_VALUE') {
        amount =
          promotion.minOrderValue != null && memoizedCartSubTotal >= promotion.minOrderValue
            ? memoizedCartSubTotal * rate
            : 0;
      } else if (promotion.ruleType === 'CATEGORY') {
        const categorySubtotal = memoizedCart.items.reduce((sum, item) => {
          const matches = item.categories?.some((category) => category.id === promotion.categoryId);
          return matches ? sum + item.price * item.quantity : sum;
        }, 0);
        amount = categorySubtotal * rate;
      }

      return amount > best ? amount : best;
    }, 0);
    //eslint-disable-next-line
  }, [memoizedCartSubTotal, existingOrders, promotions, memoizedCart, user]);

  const memoizedCartTotal = useMemo(() => {
    return (
      memoizedCartSubTotal * (1 + serviceFeePercentage) +
      (address && memoizedCartCount > 0 ? deliveryFee : 0) -
      memoizedCartDiscount
    );
    //eslint-disable-next-line
  }, [memoizedCartSubTotal, address, memoizedCartDiscount, deliveryFee, deliveryLoading]);

  useEffect(() => {
    if (!cartOpen && !isMobile) return;

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(() => {
      const storedAddress = getFromLocalStorage(STORAGE_KEYS.DELIVERY_ADDRESS);

      if (storedAddress?.location && memoizedCart.items?.length > 0) {
        dispatch(
          getDeliveryRequest({
            destinationLat: storedAddress.location.lat,
            destinationLng: storedAddress.location.lng
          })
        );
      }
    }, 300);

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
    // `location` is keyed on `address` and is the actual signal that the delivery destination
    // changed — without it, picking or changing a delivery address never re-fetches the fee
    // (cartOpen/memoizedCart don't change just because the address did), leaving delivery at 0
    // until something unrelated happens to toggle the sidebar or the cart contents.
  }, [cartOpen, isMobile, memoizedCart, location, dispatch]);

  return (
    <CartContext.Provider
      value={{
        cart: memoizedCart,
        setCart: handleSetCart,
        cartOpen,
        setCartOpen,
        cartCount: memoizedCartCount,
        addToCart: handleAddToCart,
        incrementItem: handleIncrementItem,
        decrementItem: handleDecrementItem,
        removeFromCart: handleRemoveFromCart,
        isItemInCart: handleIsItemInCart,
        updateCartItemOptions: handleUpdateCartItemOptions,
        cartTotal: memoizedCartTotal,
        address,
        setAddress,
        location,
        showOrderModal,
        setShowOrderModal,
        order,
        serviceFee,
        deliveryFee,
        deliveryLoading,
        discount: memoizedCartDiscount,
        subtotal: memoizedCartSubTotal
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
